import { useCallback, useMemo } from "react"
import searchSlice, { FilterId, McoSortingRule } from "../searchSlice"
import { useStoreDispatch, useStoreSelector } from "../store"
import { ToggleColumns } from "../SearchFilters"

export const useSearchText = (id: FilterId) => {
	const searchText = useStoreSelector((state) =>
		searchSlice.selectors.searchText(state, id)
	)
	const dispatch = useStoreDispatch()
	const setSearchText = useCallback(
		(searchText: string) =>
			dispatch(searchSlice.actions.setSearchQuery({ id, searchText })),
		[dispatch, id]
	)
	return [searchText, setSearchText] as const
}

export const useSearchFilters = (id: FilterId) => {
	const dispatch = useStoreDispatch()

	const state = useStoreSelector((state) =>
		searchSlice.selectors.state(state, id)
	)
	const filterPipeline = useStoreSelector((state) =>
		searchSlice.selectors.filterPipeline(state, id)
	)
	const updateFilterSelection = useCallback(
		(action: { filterPipeline: string; checked: boolean }) => {
			dispatch(
				searchSlice.actions.updateSelectedFilter({
					id,
					filterPipeline: action.filterPipeline,
					checked: action.checked
				})
			)
		},
		[dispatch, id]
	)
	const setSearchQuery = useCallback(
		(searchText: string) =>
			dispatch(searchSlice.actions.setSearchQuery({ id, searchText })),
		[dispatch, id]
	)
	const setPageIndex = useCallback(
		(pageIndex: number) =>
			dispatch(searchSlice.actions.setPageIndex({ id, pageIndex })),
		[dispatch, id]
	)
	const setPageSize = useCallback(
		(pageSize: number) =>
			dispatch(searchSlice.actions.setPageSize({ id, pageSize })),
		[dispatch, id]
	)

	const setToggleColumns = useCallback(
		(toggleColumns: ToggleColumns) =>
			dispatch(
				searchSlice.actions.setToggleColumns({ id, toggleColumns })
			),
		[dispatch, id]
	)

	const setSorted = useCallback(
		(sorted: McoSortingRule[]) =>
			dispatch(searchSlice.actions.setSorted({ id, sorted })),
		[dispatch, id]
	)

	return useMemo(
		() => ({
			...state,
			filterPipeline,
			updateFilterSelection,
			setSearchQuery,
			setPageIndex,
			setPageSize,
			setToggleColumns,
			setSorted
		}),
		[
			filterPipeline,
			updateFilterSelection,
			setPageIndex,
			setPageSize,
			setSearchQuery,
			setToggleColumns,
			setSorted,
			state
		]
	)
}
